/**
 * sl-67kc.3.3: Shared row-level primitives for the history-row-variants
 * system (`history-row-variants/`). Extracted so each variant can
 * render identical label/value rows + error boxes without duplicating
 * the theme-aware styling.
 *
 * SOLID:
 * - S: Each primitive owns one visual concern.
 * - D: Components read the current theme via `useTheme`; no variant
 *      has to thread theme info down.
 */
import { useTheme } from 'next-themes';
import type { FC } from 'react';

const BLUE_WHITE_ERROR = {
  bg: '#1a0f0f',
  color: '#EB4335',
};

export const ErrorBox: FC<{ message: string; size?: 'small' | 'medium' }> = ({
  message,
  size = 'medium',
}) => {
  const { resolvedTheme } = useTheme();
  const isBlueWhite = resolvedTheme === 'blue-white';

  return (
    <div
      style={{
        marginTop: size === 'medium' ? '12px' : '8px',
        padding: size === 'medium' ? '8px' : '6px',
        backgroundColor: isBlueWhite
          ? BLUE_WHITE_ERROR.bg
          : 'var(--streamlain-background-error-color)',
        borderRadius: size === 'medium' ? '6px' : '4px',
        fontSize: size === 'medium' ? '11px' : '10px',
        color: isBlueWhite
          ? BLUE_WHITE_ERROR.color
          : 'var(--streamlain-error-color)',
      }}
    >
      {message}
    </div>
  );
};

export const DataRow: FC<{
  label: string;
  value: React.ReactNode;
  color?: string;
}> = ({ label, value, color }) => (
  <div style={{ display: 'flex', fontSize: '11px', marginBottom: '4px' }}>
    <span style={{ opacity: 0.6, width: '100px' }}>{label}:</span>
    <span style={{ color, fontWeight: color ? 600 : 400 }}>{value}</span>
  </div>
);
